import { Stack, Typography } from "@mui/material";
import React, { useState, useEffect } from "react";
import moment from "moment";
import { getCardTypeIcon } from "utils/utils";
import { spinning } from "../utils";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { queryMerchantSMEUsage } from "../model";
const smeUsageCache = {};
const MerchantSMEUsage = ({ merchantId }) => {
  const [loading, setLoading] = useState(false);
  const [SMEMonthlyList, setSMEMonthlyList] = useState([]);
  const [SMEMonthlyLastUpdate, setSMEMonthlyLastUpdate] = useState("");

  const CARD_CONFIGS = [
    {
      type: "VISA",
      limit: 1950,
      gateways: ["ALLINPAY", "FUBON", "YEDPAY"],
      icon: getCardTypeIcon("VISA"),
    },
    {
      type: "MASTER",
      limit: 1560,
      gateways: ["ALLINPAY", "FUBON", "YEDPAY"],
      icon: getCardTypeIcon("MASTER"),
    },
    {
      type: "UNIONPAY",
      limit: 800,
      gateways: ["ALLINPAY", "FUBON"],
      icon: getCardTypeIcon("UNIONPAY"),
    },
    {
      type: "AMEX",
      limit: 1000,
      gateways: ["AMEX_BBMSL"],
      icon: getCardTypeIcon("AMEX"),
    },
  ];

  useEffect(() => {
    const fetchSMEMonthlyData = async () => {
      try {
        setLoading(true);

        // Check cache first
        if (smeUsageCache[merchantId]) {
          setSMEMonthlyList(smeUsageCache[merchantId].data);
          setSMEMonthlyLastUpdate(
            smeUsageCache[merchantId].lastModifiedDateTime,
          );
          return;
        }

        // Only fetch if not in cache
        const apiResponse = await queryMerchantSMEUsage(merchantId);
        console.log("apiResponse", apiResponse);
        const { data } = apiResponse;

        if (data?.responseCode === "0000" && data?.data) {
          // Update cache
          smeUsageCache[merchantId] = {
            data: data.data,
            lastModifiedDateTime: data.lastModifiedDateTime,
          };

          setSMEMonthlyList(data.data);
          setSMEMonthlyLastUpdate(data.lastModifiedDateTime);
        }
      } catch (error) {
        console.error("Error fetching merchant detail:", error);
        delete smeUsageCache[merchantId];
      } finally {
        setLoading(false);
      }
    };

    if (merchantId) {
      fetchSMEMonthlyData();
    }
  }, [merchantId]);

  const convertReportListToMonthlyObject = (cardTypeList) => {
    const monthArray = {};
    for (let i = 0; i < 12; i++) {
      const currentMonth = moment().subtract(i, "months").format("YYYY-MM");
      monthArray[currentMonth] = 0;
    }
    if (cardTypeList) {
      cardTypeList.forEach((item) => {
        monthArray[item["From"].slice(0, 7)] = item["Total Amount"];
      });
    }
    return monthArray;
  };

  const addCommaToMoneyAmount = (number) => {
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const row = (cardType, processor, SMEMonthlyList, limit) => {
    const oneYearPaymentAmountList = convertReportListToMonthlyObject(
      SMEMonthlyList.filter(
        (report) =>
          report["Card Type"] === cardType && report["Gateway"] === processor,
      ),
    );
    const sumOf12MonthsPayAmount =
      Object.values(oneYearPaymentAmountList).reduce(
        (a, b) => a + parseInt(b),
        0,
      ) / 1000;
    const result = [];
    switch (processor) {
      case "ALLINPAY":
        result.push(
          `Allinpay:${addCommaToMoneyAmount(parseInt(sumOf12MonthsPayAmount).toString())}K / ${addCommaToMoneyAmount(limit.toString())}K (${parseInt((sumOf12MonthsPayAmount * 100) / limit)}%)`,
        );
        break;
      case "FUBON":
        result.push(
          `Fubon:${addCommaToMoneyAmount(parseInt(sumOf12MonthsPayAmount).toString())}K / ${addCommaToMoneyAmount(limit.toString())}K (${parseInt((sumOf12MonthsPayAmount * 100) / limit)}%)`,
        );
        break;
      case "YEDPAY":
        result.push(
          `Yedpay:${addCommaToMoneyAmount(parseInt(sumOf12MonthsPayAmount).toString())}K / ${addCommaToMoneyAmount(limit.toString())}K (${parseInt((sumOf12MonthsPayAmount * 100) / limit)}%)`,
        );
        break;
      case "AMEX_BBMSL":
        result.push(
          `AMEX_BBMSL:${addCommaToMoneyAmount(parseInt(sumOf12MonthsPayAmount).toString())}K / ${addCommaToMoneyAmount(limit.toString())}K (${parseInt((sumOf12MonthsPayAmount * 100) / limit)}%)`,
        );
        break;
      default:
        break;
    }
    return [...result, ...Object.values(oneYearPaymentAmountList)];
  };

  const getLast12Months = () => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      months.push(moment().subtract(i, "months").format("YYYY-MM"));
    }
    return months;
  };

  const generateTableData = (config) => {
    const headerList = [config.icon, ...getLast12Months()];
    const rowList = config.gateways.map((gateway) =>
      row(config.type, gateway, SMEMonthlyList, config.limit),
    );
    return { headerList, rowList };
  };

  const SMEUsageTalbe = ({ headerList, rowList }) => {
    return (
      <TableContainer sx={{ marginBottom: "3rem" }}>
        <Table sx={{ minWidth: 700 }} aria-label="spanning table">
          {/* header */}
          <TableHead>
            <TableRow>
              {headerList.map((item, index) => {
                if (index === 0) {
                  return (
                    <TableCell
                      key={index}
                      sx={{ background: "#f3f7f7", minWidth: "100px" }}
                    >
                      <img src={item} alt="visa_logo" height={36} />
                    </TableCell>
                  );
                } else {
                  return (
                    <TableCell
                      align="right"
                      key={index}
                      sx={{ background: "#f3f7f7" }}
                    >
                      <Typography>{item}</Typography>
                    </TableCell>
                  );
                }
              })}
            </TableRow>
          </TableHead>

          {/* body */}
          <TableBody>
            {rowList.map((row) => {
              return (
                <TableRow key={row.desc}>
                  {row.map((item, index) => {
                    if (index === 0) {
                      return (
                        <TableCell key={index} align="left">
                          <Typography>{item.split(":")[0]}</Typography>
                          <Typography>{item.split(":")[1]}</Typography>
                        </TableCell>
                      );
                    } else if (index <= 12) {
                      return (
                        <TableCell align="right" key={index}>
                          <Typography>
                            {Number(item ?? "0").toLocaleString("en-US", {
                              maximumFractionDigits: 0,
                            })}
                          </Typography>
                        </TableCell>
                      );
                    }
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Stack direction="column" sx={{ padding: "18px", background: "white" }}>
      {loading ? (
        spinning()
      ) : (
        <>
          <Stack direction="row" justifyContent={"flex-end"} sx={{ mb: 2 }}>
            <Typography>
              {`Last update: ${moment(SMEMonthlyLastUpdate).format("YYYY-MM-DD HH:mm")}`}
            </Typography>
          </Stack>
          {CARD_CONFIGS.map((config) => (
            <SMEUsageTalbe key={config.type} {...generateTableData(config)} />
          ))}
        </>
      )}
    </Stack>
  );
};

export default React.memo(MerchantSMEUsage);
